import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { ChevronDown } from 'lucide-react'
import { format } from 'date-fns'

import { Button } from '@repo/ui'

import { ProductSyncModeMenu, getProductSyncMode } from 'components'

import type { ProductSyncMode } from 'types'

interface ProductSyncButtonProps {
  /** Current per-product auto-sync policy (VRG-102 sync_mode). */
  mode: ProductSyncMode
  /** Manual one-off pull from the store (bypasses the auto policy — explicit action). */
  onSyncNow: () => void
  /** Change the persistent auto-sync policy; persists per product. */
  onChangeMode: (mode: ProductSyncMode) => void
  syncing?: boolean
  /** Product's last-updated timestamp (proxy for last sync — bumps on any edit too). */
  lastUpdatedAt?: string | Date | null
}

/**
 * VRG-102 — split "Sync" button (matches the secondary Edit button). Main click =
 * **Sync now** (manual pull); the caret opens an **Auto-sync** menu to set the
 * persistent per-product policy (inherit / always_sync / locked), remembered on the
 * product. The menu inherits the house menu-item styling from the theme.
 */
export const ProductSyncButton = ({
  mode,
  onSyncNow,
  onChangeMode,
  syncing,
  lastUpdatedAt,
}: ProductSyncButtonProps) => {
  const current = getProductSyncMode(mode)
  const CurrentIcon = current.icon

  const lastUpdatedLabel = lastUpdatedAt
    ? format(new Date(lastUpdatedAt), 'MMM d, yyyy · h:mm a')
    : null

  const syncTooltip = (
    <Stack spacing={0.25} sx={{ py: 0.25 }}>
      <Typography variant="body4">Auto-sync: {current.label}</Typography>
      <Typography variant="body4" sx={{ opacity: 0.8 }}>
        {lastUpdatedLabel ? `Last updated ${lastUpdatedLabel}` : 'Not synced yet'}
      </Typography>
    </Stack>
  )

  return (
    <Stack direction="row" alignItems="stretch">
      {/* Main action — Sync now. Secondary variant = same colour as Edit. The leading
          icon carries WHICH auto-sync mode is active (VRG-103) — the label stays
          "Sync"/"Syncing…" so this still reads as an ACTION button, not a state
          readout; the mode itself is state, conveyed via the icon + this tooltip.
          @repo/ui Button doesn't forward a ref, so each Tooltip wraps a ref-holding
          inline-flex span (also lets the tooltip show while the button is disabled). */}
      <Tooltip title={syncTooltip}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            variant="secondary"
            disableElevation
            disabled={syncing}
            onClick={onSyncNow}
            aria-label={`Sync now — auto-sync: ${current.label}`}
            startIcon={
              <CurrentIcon strokeWidth={1} size={16} className={syncing ? 'animate-spin' : undefined} />
            }
            sx={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: 'none',
              // The base "border" shorthand set by @repo/ui's Button on :hover/:focus/
              // :active re-applies all four sides, which silently brought the right
              // border back on hover — a white hairline flashing in between Sync and
              // the caret. `&&` doubles the selector's specificity so this wins over
              // that nested hover/focus/active rule regardless of merge order (Button
              // merges `sx` with `{...finalStyles, ...sx}`, a SHALLOW merge — a plain
              // top-level `&:hover` key here would silently replace, not extend, the
              // component's own hover styling instead of layering on top of it).
              // The caret's own left border remains the single hairline divider.
              '&&:hover, &&:focus, &&:focus-visible, &&:active': {
                borderRightWidth: 0,
              },
            }}
          >
            {syncing ? 'Syncing…' : 'Sync'}
          </Button>
        </Box>
      </Tooltip>
      {/* Caret — open the Auto-sync policy menu. Its own left border IS the divider
          between the two buttons (was ALSO a 1px gap + the Sync button's own right
          border, which doubled up into a thick seam instead of one hairline). */}
      <ProductSyncModeMenu
        mode={mode}
        onChangeMode={onChangeMode}
        renderTrigger={({ onClick }) => (
          <Tooltip title="Change auto-sync">
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button
                variant="secondary"
                disableElevation
                onClick={onClick}
                aria-label="Auto-sync options"
                sx={{
                  px: 2,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
              >
                <ChevronDown strokeWidth={1} size={16} />
              </Button>
            </Box>
          </Tooltip>
        )}
      />
    </Stack>
  )
}
